import {
  FieldErrors,
  UseFormRegister,
  UseFormUnregister,
  UseFormWatch,
} from "react-hook-form";
import { FormTypes } from "../../types/FormTypes";
import { Tooltip as ReactTooltip } from "react-tooltip";
import InputsOrientation from "../Inputs/InputsOrientation";
import InputsDriveSystem from "../Inputs/InputsDriveSystem";
import InputsPayload from "../Inputs/InputsPayload";
import InputsMotionProfile from "../Inputs/InputsMotionProfile";
import InputsSetMaximums from "../Inputs/InputsSetMaximums";
import InputsCycle from "../Inputs/InputsCycle";
import InputsTemperatureRange from "../Inputs/InputsTemperatureRange";
import InputsAdjustmentFactor from "../Inputs/InputsAdjustmentFactor";
import CompareSideBar from "./CompareSideBar";
import Errors from "./Errors";
import { useEffect } from "react";

interface IInputs {
  register: UseFormRegister<FormTypes>;
  unregister: UseFormUnregister<FormTypes>;
  errors: FieldErrors<FormTypes>;
  watch: UseFormWatch<FormTypes>;
  nextStep: Function;
  compareItemArray: Array<any>;
  handleCheckboxChange: Function;
  handleCheckNone: Function;
  selectedItemsToCompare: Array<any>;
  formData: any;
  setItemToConfigure: Function;
  setAnyStep: Function;
  setValue: Function;
  setDataCheckingModeHandler: Function;
  dataCheckingMode: boolean;
  trigger: Function;
  onBlurHandler: (event: any) => void;
  externalForceCount: number;
  setExternalForceCount: Function;
  momentsCount: number;
  setMomentsCount: Function;
  travelDistance: number;
}

function Inputs(props: IInputs) {
  const {
    register,
    unregister,
    errors,
    nextStep,
    watch,
    compareItemArray,
    handleCheckboxChange,
    handleCheckNone,
    selectedItemsToCompare,
    formData,
    setDataCheckingModeHandler,
    dataCheckingMode,
    trigger,
    onBlurHandler,
    externalForceCount,
    setExternalForceCount,
    momentsCount,
    setMomentsCount,
    travelDistance,
  } = props;

  useEffect(() => {
    trigger();
  }, [trigger]);

  return (
    <div className="d-flex">
      <div
        style={{
          width: "80%",
          borderRight: "1px solid lightgray",
          paddingRight: "15px",
        }}
      >
        <h3 className="text-left styled-font styled-font">
          Please provide input details for your project
        </h3>
        {/* Orientation */}
        <div className="padding-bottom border-bottom">
          <h4 className="text-left styled-font">Desired orientation</h4>
          <InputsOrientation
            register={register}
            errors={errors}
            onBlurHandler={onBlurHandler}
          />
        </div>
        {/* Travel Distance */}
        <div className="padding-bottom border-bottom text-left ">
          <div className="title-with-info-wrapper">
            <h4 className="styled-font">Travel Distance</h4>{" "}
            <i
              className="fas fa-info-circle"
              data-tooltip-id="travelDistance-tooltip"
            ></i>
          </div>
          <div className="d-flex w-100px">
            <input
              type="number"
              step="any"
              className={errors.travelDistance ? "input-error" : ""}
              {...register("travelDistance", {
                required: true,
                valueAsNumber: true,
                onBlur: onBlurHandler,
                min: 100,
                max: 99999,
              })}
            />
            <p style={{ marginBottom: "0px", marginLeft: "5px" }}>mm</p>
          </div>
          <small className="input-small-text">100 - 99999 mm</small>
          <ReactTooltip
            id="travelDistance-tooltip"
            place="right"
            style={{ maxWidth: "50%" }}
          >
            <span>
              The maximum range of motion that the system is capable of.
            </span>
          </ReactTooltip>
        </div>
        {/* Drive System */}
        <div className="padding-bottom border-bottom text-left ">
          <div className="title-with-info-wrapper">
            <h4 className="styled-font">Drive System</h4>
            <i
              className="fas fa-info-circle"
              data-tooltip-id="driveSystem-tooltip"
            ></i>
          </div>
          <InputsDriveSystem
            register={register}
            errors={errors}
            onBlurHandler={onBlurHandler}
          />
          <small className="input-small-text">Must select one or more</small>
          <ReactTooltip
            id="driveSystem-tooltip"
            style={{ maxWidth: "50%" }}
            place="right"
          >
            <span>Choose which drive mechanisms to include in results:</span>
            <br></br>
            <br></br>
            <span>
              <b>Belt:</b>
            </span>
            <br></br>
            <span>
              [AT-style steel-reinforced polyurethane belt on aluminum pulleys]
            </span>
            <br></br>
            <b>Best option for high speeds or overall cost effectiveness.</b>
            <br></br>
            <span>Capable of long travel lengths.</span>
            <br></br>
            <br></br>
            <span>
              <b>Chain:</b>
            </span>
            <br></br>
            <span>[Steel chain on steel sprockets]</span>
            <br></br>
            <span>
              <b>
                Best option for vertical hoisting, high temperatures, or extreme
                debris (metal shards, heavy particulate, etc.). Capable of long
                travel lengths.
              </b>
            </span>
            <br></br>
            <br></br>
            <span>
              <b>Lead Screw:</b>
            </span>
            <br></br>
            <span>[Polymer nut on precision machined screw]</span>
            <br></br>
            <span>
              <b>Best option for high linear precision.</b>
            </span>
            <br></br>
            <span>
              Compared to ball screw: typically more cost effective; capable of
              higher speeds; has non-backdrivable options for vertical
              applications. Typically not suitable for continuous duty
              applications due to friction on the polymer nut.
            </span>
            <br></br>
            <br></br>
            <span>
              <b>Ball Screw:</b>
            </span>
            <br></br>
            <span>[Steel ball bearings on rolled precision screw]</span>
            <br></br>
            <span>
              <b>Highest thrust capacity.</b>
            </span>
            <br></br>
            <span>
              Compared to lead screw: higher efficiency; capable of continuous
              duty cycle. Typically not suitable for vertical applications due
              to backdrivability.
            </span>
          </ReactTooltip>
        </div>
        {/* Payload */}
        <div className="padding-bottom border-bottom text-left ">
          <div className="title-with-info-wrapper">
            <h4 className="styled-font">Payload</h4>
            <i
              className="fas fa-info-circle"
              data-tooltip-id="payload-tooltip"
            ></i>
          </div>
          <InputsPayload
            register={register}
            unregister={unregister}
            errors={errors}
            watch={watch}
            onBlurHandler={onBlurHandler}
            externalForceCount={externalForceCount}
            setExternalForceCount={setExternalForceCount}
            momentsCount={momentsCount}
            setMomentsCount={setMomentsCount}
          />
          <ReactTooltip
            id="payload-tooltip"
            place="right"
            style={{ maxWidth: "50%" }}
          >
            <span>
              When specifying a motion system, any forces applied on the
              carriage need to be considered, including inertial forces,
              gravitational forces, and external forces such as tool pressure,
              impact loading, and payload.
            </span>
            <br></br>
            <br></br>
            <span>This section is used to calculate:</span>
            <ul style={{ listStyle: "inside" }}>
              <li>Net forces and moments on the bearings</li>
              <li>Factor of Safety of the bearings</li>
              <li>Estimated life of the bearings</li>
              <li>Net thrust force on the drive system</li>
              <li>Factor of Safety of the drive system</li>
            </ul>
            <span>
              The X, Y, and Z axes follow the 'right hand rule' of Cartesian
              coordinate systems, and are global (not defined by the actuator's
              orientation). Z is always 'up', toward the sky. If system
              orientation is 'Horizontal', 'Rolled', or 'Inverted', the actuator
              direction of motion will be aligned with the X axis. The origin of
              the global coordinate system is the center of the carriage's
              mounting face.
            </span>
            <br></br>
            <br></br>
            <span>
              Inertial and gravitational forces and moments from the payload
              will be automatically calculated using the system orientation,
              payload mass and offset, and the acceleration of the system. The
              magnitude and offset position of applied forces and/or the
              magnitude of applied moments can be added to account for such
              things as a downward force applied by an assembly presses, the
              moment applied by external rotary tools, etc.
            </span>
            <br></br>
            <br></br>
            <span>
              Alternatively, to express a Free Body Diagram of pre-calculated
              net forces and moments experienced by the system's carriage, add
              one force per direction (X, Y, Z) with no offsets and fill in the
              X, Y, and Z moment fields.
            </span>
            <br></br>
            <br></br>
            <span>
              We recommend using your best estimate of maximum payload mass
              (including any hardware used to affix the product, tool, etc. to
              the carriage); payload Center of Mass position (XYZ offsets); and
              applied forces & moments if applicable.
            </span>
          </ReactTooltip>
        </div>
        {/* Motion Profile */}
        {/* <div className="padding-bottom border-bottom text-left ">
          <div className="title-with-info-wrapper">
            <h4 className="styled-font">Motion Profile</h4>
            <i
              className="fas fa-info-circle"
              data-tooltip-id="motionProfile-tooltip"
            ></i>
          </div>
          <InputsMotionProfile
            register={register}
            errors={errors}
            setValue={setValue}
            onBlurHandler={onBlurHandler}
          />
          <ReactTooltip
            id="motionProfile-tooltip"
            place="right"
            content="Motion profile tooltip"
          />
        </div>
        {/* Motion Profile - Formerly Set Maximums (reduced scope) */}
        <div className="padding-bottom border-bottom text-left ">
          <div className="title-with-info-wrapper">
            <h4 className="styled-font">Motion Profile</h4>
            <i
              className="fas fa-info-circle"
              data-tooltip-id="setMaximums-tooltip"
            ></i>
          </div>
          <InputsSetMaximums
            register={register}
            errors={errors}
            formData={formData}
            onBlurHandler={onBlurHandler}
          />
          <ReactTooltip
            id="setMaximums-tooltip"
            place="right"
            style={{ maxWidth: "50%" }}
          >
            <span>
              Cycle Stroke is the distance of motion in one direction for an
              average cycle. Cannot exceed the system's overall travel distance.
              Used in combination with duty cycle to estimate the life of the
              system's bearings in months.
            </span>
            <br></br>
            <br></br>
            <span>
              Max Velocity of the application is used to assess various drive
              types and sizes. If unknown, provide your best estimat
            </span>
            <br></br>
            <br></br>
            <span>
              Max Acceleration of the application is used in conjunction with
              payload to calculate the dynamic forces on the system. If unknown,
              provide your best estimate.
            </span>
          </ReactTooltip>
        </div>
        {/* Life / Duty Cycle */}
        <div className="padding-bottom border-bottom text-left ">
          <div className="title-with-info-wrapper">
            <h4 className="styled-font">Life / Duty Cycle</h4>
            <i
              className="fas fa-info-circle"
              data-tooltip-id="cycle-tooltip"
            ></i>
          </div>
          <InputsCycle
            register={register}
            errors={errors}
            watch={watch}
            onBlurHandler={onBlurHandler}
          />
          <ReactTooltip
            id="cycle-tooltip"
            place="right"
            style={{ maxWidth: "50%" }}
          >
            <span>
              Optional; used in combination with stroke to estimate the life of
              the system's bearings in months. If left blank, bearing life
              estimate will be computed in kilometers. We recommend using your
              best estimate of typical usage.
            </span>
          </ReactTooltip>
        </div>
        {/* Temperature Range */}
        <div className="padding-bottom border-bottom text-left ">
          <div className="title-with-info-wrapper">
            <h4 className="styled-font">Temperature Range</h4>
            <i
              className="fas fa-info-circle"
              data-tooltip-id="temperatureRange-tooltip"
            ></i>
          </div>
          <InputsTemperatureRange
            register={register}
            errors={errors}
            watch={watch}
            onBlurHandler={onBlurHandler}
          />
          <ReactTooltip
            id="temperatureRange-tooltip"
            style={{ maxWidth: "50%" }}
            place="right"
          >
            <span>
              Optional; only systems that are capable of the minimum temperature
              and the maximum temperature will be included in the results.
            </span>
            <span>
              If left blank, no results will be excluded based on temperature
              requirements.
            </span>
          </ReactTooltip>
        </div>
        {/* Adjustment Factor */}
        <div className="padding-bottom border-bottom text-left ">
          <div className="title-with-info-wrapper">
            <h4 className="styled-font">Adjustment Factor</h4>
            <i
              className="fas fa-info-circle"
              data-tooltip-id="adjustmentFactor-tooltip"
            ></i>
          </div>
          <InputsAdjustmentFactor
            register={register}
            errors={errors}
            onBlurHandler={onBlurHandler}
          />
          <ReactTooltip
            id="adjustmentFactor-tooltip"
            place="right"
            style={{ maxWidth: "50%" }}
          >
            <span>
              The "Adjustment Factor" is used in calculating bearing life, and
              ranges from 0.1 to 1. Harsher conditions reduce the Adjustment
              Factor, and result in a lower estimated life.
            </span>
            <table className="adjustment-factor-tooltip">
              <tbody>
                <tr>
                  <th>Conditions</th>
                  <th style={{ width: "20%" }}>
                    A<sub>F</sub>
                  </th>
                </tr>
                <tr>
                  <td>
                    Clean, adequate lubrication, low duty, low shock, low
                    vibration
                  </td>
                  <td>1.0 – 0.7</td>
                </tr>
                <tr>
                  <td>
                    Moderate contamination, medium duty, medium shock, low to
                    medium vibration
                  </td>
                  <td>0.7 – 0.4</td>
                </tr>
                <tr>
                  <td>
                    Heavy contamination, limited lubrication, high duty, high
                    acceleration, medium to high shock, high vibration
                  </td>
                  <td>0.4 - 0.1</td>
                </tr>
              </tbody>
            </table>
          </ReactTooltip>
        </div>
        <div className="padding-top text-right"></div>
      </div>
      {/* Compare Side Bar  */}
      <div id="sticky-sidebar" style={{ width: "20%", paddingLeft: "15px" }}>
        <div
          style={{
            maxHeight: "50vh",
            overflowY: "scroll",
            padding: "15px",
            overflowX: "hidden",
            borderBottom: "1px solid lightgray",
          }}
          id="dynamic-height-sidebar"
        >
          <CompareSideBar
            compareItemArray={compareItemArray}
            handleCheckboxChange={handleCheckboxChange}
            handleCheckNone={handleCheckNone}
            selectedItemsToCompare={selectedItemsToCompare}
            travelDistance={travelDistance}
          />
        </div>
        {Object.keys(errors).length ? (
          <>
            {!dataCheckingMode ? (
              <>
                <button
                  type="button"
                  style={{ paddingLeft: "5px", paddingRight: "5px" }}
                  className="orange-button margin-top w-100"
                  onClick={() => setDataCheckingModeHandler()}
                >
                  NEXT: CHECK DATA
                </button>
                <p className="text-danger mt2">
                  Please complete the required information to view product
                  results.
                </p>
              </>
            ) : null}
          </>
        ) : (
          <>
            {!dataCheckingMode ? (
              <>
                <button
                  disabled={!selectedItemsToCompare.length ? true : false}
                  className="orange-button margin-top w-100"
                  onClick={() => nextStep()}
                >
                  NEXT: COMPARE
                </button>
                {!selectedItemsToCompare.length ? (
                  <p className="text-danger mt2">
                    Select products to compare OR configure a product without
                    comparing.
                  </p>
                ) : null}
              </>
            ) : null}
          </>
        )}
        {dataCheckingMode ? (
          <>
            <button
              disabled={!selectedItemsToCompare.length ? true : false}
              className="orange-button margin-top w-100"
              onClick={() => nextStep()}
            >
              NEXT: COMPARE
            </button>
            <Errors errors={errors} />
          </>
        ) : null}
      </div>
    </div>
  );
}

export default Inputs;
